<template>
    <div class="el-date-range-picker__content is-left">
        <div class="el-date-range-picker__header">
            <AGlyph
                name="chevron-double-left--16"
                class="el-date-picker__header-btn"
                :aria-label="leftCurrentView === 'year' ? 'Previous Decade' : 'Previous Year'"
                @click="leftPrevYear"
            />
            <AGlyph
                v-if="leftCurrentView === 'date'"
                name="i-chevron-left--16"
                class="el-date-picker__header-btn el-date-picker__header-btn--with-spacer-left"
                aria-label="Previous Month"
                @click="leftPrevMonth"
            />
            <span class="el-date-picker__label-wrapper">
                <span
                    v-show="leftCurrentView === 'date'"
                    role="button"
                    class="el-date-picker__header-label el-date-picker__header-label-month"
                    :class="{ active: leftCurrentView === 'month' }"
                    @click="leftShowMonth"
                >
                    {{ leftMonthLabel }}
                </span>
                <span
                    role="button"
                    class="el-date-picker__header-label el-date-picker__header-label-year"
                    :class="{
                        disabled: leftCurrentView === 'year',
                        'el-date-picker__header-label-year--full-width': leftCurrentView === 'year',
                    }"
                    @click="leftShowYear"
                >
                    {{ leftYearLabel }}
                </span>
            </span>
            <AGlyph
                v-if="leftCurrentView === 'date'"
                name="i-chevron-right--16"
                class="el-date-picker__header-btn el-date-picker__header-btn--with-spacer-right"
                aria-label="Next Month"
                @click="leftNextMonth"
            />
            <AGlyph
                name="chevron-double-right--16"
                class="el-date-picker__header-btn"
                :aria-label="leftCurrentView === 'year' ? 'Next Decade' : 'Next Year'"
                @click="leftNextYear"
            />
        </div>

        <DateTable
            v-show="leftCurrentView === 'date'"
            :first-day-of-week="firstDayOfWeek"
            :panel-date="leftDate"
            :range-start="effectiveRange.start"
            :range-end="effectiveRange.end"
            @pick="onPickLeft"
            @hover="onHoverLeft"
        />
        <MonthTable
            v-show="leftCurrentView === 'month'"
            :date="leftDate"
            @pick="onPickLeftMonth"
        />
        <YearTable
            v-show="leftCurrentView === 'year'"
            :date="leftDate"
            @pick="onPickLeftYear"
        />
    </div>

    <div class="el-date-range-picker__content is-right">
        <div class="el-date-range-picker__header">
            <AGlyph
                name="chevron-double-left--16"
                class="el-date-picker__header-btn"
                :aria-label="rightCurrentView === 'year' ? 'Previous Decade' : 'Previous Year'"
                @click="rightPrevYear"
            />
            <AGlyph
                v-if="rightCurrentView === 'date'"
                name="i-chevron-left--16"
                class="el-date-picker__header-btn el-date-picker__header-btn--with-spacer-left"
                aria-label="Previous Month"
                @click="rightPrevMonth"
            />
            <span class="el-date-picker__label-wrapper">
                <span
                    v-show="rightCurrentView === 'date'"
                    role="button"
                    class="el-date-picker__header-label el-date-picker__header-label-month"
                    :class="{ active: rightCurrentView === 'month' }"
                    @click="rightShowMonth"
                >
                    {{ rightMonthLabel }}
                </span>
                <span
                    role="button"
                    class="el-date-picker__header-label el-date-picker__header-label-year"
                    :class="{
                        disabled: rightCurrentView === 'year',
                        'el-date-picker__header-label-year--full-width': rightCurrentView === 'year',
                    }"
                    @click="rightShowYear"
                >
                    {{ rightYearLabel }}
                </span>
            </span>
            <AGlyph
                v-if="rightCurrentView === 'date'"
                name="i-chevron-right--16"
                class="el-date-picker__header-btn el-date-picker__header-btn--with-spacer-right"
                aria-label="Next Month"
                @click="rightNextMonth"
            />
            <AGlyph
                name="chevron-double-right--16"
                class="el-date-picker__header-btn"
                :aria-label="rightCurrentView === 'year' ? 'Next Decade' : 'Next Year'"
                @click="rightNextYear"
            />
        </div>

        <DateTable
            v-show="rightCurrentView === 'date'"
            :first-day-of-week="firstDayOfWeek"
            :panel-date="rightDate"
            :range-start="effectiveRange.start"
            :range-end="effectiveRange.end"
            @pick="onPickRight"
            @hover="onHoverRight"
        />
        <MonthTable
            v-show="rightCurrentView === 'month'"
            :date="rightDate"
            @pick="onPickRightMonth"
        />
        <YearTable
            v-show="rightCurrentView === 'year'"
            :date="rightDate"
            @pick="onPickRightYear"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import DateTable from '@core/components/date-picker/basic/DateTable.vue';
import MonthTable from '@core/components/date-picker/basic/MonthTable.vue';
import YearTable from '@core/components/date-picker/basic/YearTable.vue';
import {
    prevMonth,
    nextMonth,
    prevYear,
    nextYear,
    clearTime,
} from '@core/components/date-picker/util/index';
import AGlyph from '@core/components/glyph/glyph.vue';

const props = withDefaults(
    defineProps<{
    start: Date | null;
    end: Date | null;
    firstDayOfWeek?: number;
    unlinkPanels?: boolean;
  }>(),
    { firstDayOfWeek: 1, unlinkPanels: false },
);

const emit = defineEmits<{
  (e: 'update:start', d: Date | null): void;
  (e: 'update:end', d: Date | null): void;
  (e: 'pick', range: [Date, Date]): void;
  (e: 'apply'): void;
}>();

const modelStart = ref<Date | null>(props.start);
const modelEnd = ref<Date | null>(props.end);

watch(
    () => props.start,
    (v) => (modelStart.value = v),
);
watch(
    () => props.end,
    (v) => (modelEnd.value = v),
);

const today = new Date();
const leftDate = ref(new Date(today.getFullYear(), today.getMonth(), 1));
const rightDate = ref(new Date(today.getFullYear(), today.getMonth() + 1, 1));

const leftCurrentView = ref<'date' | 'month' | 'year'>('date');
const rightCurrentView = ref<'date' | 'month' | 'year'>('date');

const leftMonthLabel = computed(() =>
    leftDate.value.toLocaleString('en-US', { month: 'short' }),
);
const rightMonthLabel = computed(() =>
    rightDate.value.toLocaleString('en-US', { month: 'short' }),
);

function decadeStart(d: Date) {
    const y = d.getFullYear();
    return Math.floor(y / 10) * 10 - 1;
}

const leftYearLabel = computed(() => {
    if (leftCurrentView.value === 'year') {
        const s = decadeStart(leftDate.value);
        return `${s + 1} - ${s + 10}`;
    }
    return String(leftDate.value.getFullYear());
});

const rightYearLabel = computed(() => {
    if (rightCurrentView.value === 'year') {
        const s = decadeStart(rightDate.value);
        return `${s + 1} - ${s + 10}`;
    }
    return String(rightDate.value.getFullYear());
});

const hoverDate = ref<Date | null>(null);

const effectiveRange = computed(() => {
    const s = modelStart.value ? clearTime(modelStart.value) : null;
    const rawEnd = modelEnd.value ?? hoverDate.value ?? null;
    const e = rawEnd ? clearTime(rawEnd) : null;
    if (s && e)
        return s.getTime() <= e.getTime() ? { start: s, end: e } : { start: e, end: s };
    return { start: s, end: e };
});

function leftPrevMonth() {
    leftDate.value = prevMonth(leftDate.value);
    if (!props.unlinkPanels) rightDate.value = nextMonth(leftDate.value);
}
function leftNextMonth() {
    leftDate.value = nextMonth(leftDate.value);
    if (!props.unlinkPanels) rightDate.value = nextMonth(leftDate.value);
}

// DOUBLE CHEVRONS: ±10yrs in year view, ±1yr otherwise
function leftPrevYear() {
    const step = leftCurrentView.value === 'year' ? 10 : 1;
    leftDate.value = prevYear(leftDate.value, step);
    if (!props.unlinkPanels) rightDate.value = nextMonth(leftDate.value);
}
function leftNextYear() {
    const step = leftCurrentView.value === 'year' ? 10 : 1;
    leftDate.value = nextYear(leftDate.value, step);
    if (!props.unlinkPanels) rightDate.value = nextMonth(leftDate.value);
}
function rightPrevMonth() {
    rightDate.value = prevMonth(rightDate.value);
}
function rightNextMonth() {
    rightDate.value = nextMonth(rightDate.value);
}
function rightPrevYear() {
    const step = rightCurrentView.value === 'year' ? 10 : 1;
    rightDate.value = prevYear(rightDate.value, step);
}
function rightNextYear() {
    const step = rightCurrentView.value === 'year' ? 10 : 1;
    rightDate.value = nextYear(rightDate.value, step);
}

function leftShowMonth() {
    leftCurrentView.value = 'month';
}
function leftShowYear() {
    leftCurrentView.value = 'year';
}
function rightShowMonth() {
    rightCurrentView.value = 'month';
}
function rightShowYear() {
    rightCurrentView.value = 'year';
}

function beginNewRange(d: Date) {
    modelStart.value = d;
    modelEnd.value = null;
    hoverDate.value = null;
    emit('update:start', modelStart.value);
    emit('update:end', modelEnd.value);
}
function finalizeRange(a: Date, b: Date) {
    const s = clearTime(a);
    const e = clearTime(b);
    const [min, max] = s.getTime() <= e.getTime() ? [s, e] : [e, s];
    modelStart.value = min;
    modelEnd.value = max;
    emit('update:start', modelStart.value);
    emit('update:end', modelEnd.value);
    emit('pick', [modelStart.value!, modelEnd.value!]);
    hoverDate.value = null;
}

function onPickLeft(d: Date) {
    return !modelStart.value || modelEnd.value
        ? beginNewRange(d)
        : finalizeRange(modelStart.value, d);
}

function onPickRight(d: Date) {
    onPickLeft(d);
}
function onHoverLeft(d: Date) {
    if (modelStart.value && !modelEnd.value) hoverDate.value = d;
}
function onHoverRight(d: Date) {
    if (modelStart.value && !modelEnd.value) hoverDate.value = d;
}

function onPickLeftMonth(d: Date) {
    leftDate.value = new Date(leftDate.value.getFullYear(), d.getMonth(), 1);
    leftCurrentView.value = 'date';
}
function onPickRightMonth(d: Date) {
    rightDate.value = new Date(rightDate.value.getFullYear(), d.getMonth(), 1);
    rightCurrentView.value = 'date';
}
function onPickLeftYear(d: Date) {
    leftDate.value = new Date(d.getFullYear(), leftDate.value.getMonth(), 1);
    leftCurrentView.value = 'month';
}
function onPickRightYear(d: Date) {
    rightDate.value = new Date(d.getFullYear(), rightDate.value.getMonth(), 1);
    rightCurrentView.value = 'month';
}
</script>
